



































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { Element as CanvasElement, ElementActionsKeys, ElementActionsPayload } from '../../../store/types/element';
import { RootGettersReturns } from '@/store/types';
import { Editor, EditorContent } from 'tiptap';
import TextToolbar from '../Toolbars/TextToolbar.vue';
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Strike,
  Underline,
  History,
} from 'tiptap-extensions';
import Link from '@/plugins/tiptap-link';

@Component({
  components: {
    EditorContent,
    TextToolbar,
  },
})
export default class TextElement extends Vue {
  @Prop({ type: [String, Object] }) text!: string | object;
  @Prop(Boolean) selected!: boolean;
  @Prop(Boolean) doubleSelected!: boolean;
  @Prop({ type: Object }) element!: CanvasElement;

  @Action(ElementActionsKeys.SET_DOUBLE_SELECTED) setDouble!: (
    payload: ElementActionsPayload['SET_DOUBLE_SELECTED']
  ) => void;
  @Action(ElementActionsKeys.UPDATE_ELEMENT) updateElement!: (payload: ElementActionsPayload['UPDATE_ELEMENT']) => void;

  @Getter stageState!: RootGettersReturns['stageState'];
  @Getter isGuest!: RootGettersReturns['isGuest'];
  @Getter currentUser!: RootGettersReturns['currentUser'];
  @Getter sessionUsers!: RootGettersReturns['sessionUsers'];
  @Getter sharedBoard!: RootGettersReturns['sharedBoard'];

  get tooltipText() {
    const id = (this.element.data as { usedBy: number | boolean }).usedBy;
    const user = this.sessionUsers.find(user => user.id === id);
    if (user) return `${user.fullName} is typing`;
    return 'Element is shared';
  }

  tooltipColor(id: number) {
    const user = this.sessionUsers.find(user => user.id === id);
    if (user) return user.colorCode;
    return 'currentColor';
  }

  get isShared() {
    return (this.element.data as { shared: boolean }).shared;
  }

  get isUsed() {
    return !!(this.element.data as { usedBy: number | boolean }).usedBy;
  }

  get isLocked() {
    return (
      (this.element.data as { usedBy: number | boolean }).usedBy &&
      (this.element.data as { usedBy: number }).usedBy !== this.currentUser?.id
    );
  }

  snackbar = false;
  snackbarText = '';
  snackbarTimeout = 2000;

  @Watch('element', { deep: true })
  elementUpdate(newVal: CanvasElement, oldVal: CanvasElement) {
    const usedBy = (newVal.data as { usedBy: number }).usedBy;
    const shared = (newVal.data as { shared: boolean }).shared;
    const wasShared = (oldVal.data as { shared: boolean }).shared;
    if ((!shared && wasShared) || (usedBy && usedBy !== this.currentUser?.id)) {
      this.unlock();
    }
    if (!shared && wasShared && this.selected && this.isGuest) {
      this.snackbar = true;
      this.snackbarText = 'Element is no longer shared';
      this.snackbarTimeout = 2000;
    }
  }

  @Watch('doubleSelected')
  doubleClicked(newVal: boolean) {
    this.$parent.$parent.$emit('changeMoveable', !newVal);
    this.setDouble(newVal);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editor = this.editor as any;
    if (newVal) {
      if (!(this.element.data as { usedBy: number }).usedBy) {
        this.updateElement({
          ...this.element,
          data: {
            ...this.element.data,
            usedBy: this.currentUser?.id,
          },
        });
      }
      editor?.setOptions({ editable: !this.sharedBoard && (this.isShared || !this.isGuest) && !this.isLocked });
      editor?.focus();
      this.resetTimer();
    } else {
      this.unlock();
    }
  }

  private wasSelected = false;

  @Watch('selected')
  async isSelected(newVal: boolean) {
    if (this.wasSelected != newVal) {
      console.log(this.wasSelected);
      this.wasSelected = newVal;
      if (!newVal) {
        this.setDouble(newVal);
        if (!JSON.stringify(this.text).includes('text') && !(this.text && typeof this.text == 'string')) {
          this.updateElement({
            ...this.element,
            deleted: true,
          });
        }
      }
    }
  }

  private newText = this.text;

  @Watch('text')
  updateText(newVal: string) {
    if (!this.doubleSelected) {
      this.editor?.setContent(newVal);
    }
  }

  editor: null | Editor = null;
  mounted() {
    this.editor = new Editor({
      content: this.text,
      editable: false,
      onUpdate: this.onInput,
      extensions: [
        new Blockquote(),
        new CodeBlock(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3, 4, 5, 6] }),
        new BulletList(),
        new OrderedList(),
        new ListItem(),
        new TodoItem(),
        new TodoList(),
        new Bold(),
        new Code(),
        new Italic(),
        new Link(),
        new Strike(),
        new Underline(),
        new History(),
      ],
    });
  }

  beforeDestroy() {
    this.setDouble(false);
    this.editor?.destroy();
  }

  timeout!: NodeJS.Timeout;

  resetTimer() {
    if (!this.isShared && !this.isLocked) return;
    clearTimeout(this.timeout);
    this.timeout = setTimeout(this.unlock, 60 * 1000); // 60 sec
  }

  unlock() {
    if (this.isShared && !this.isLocked) {
      clearTimeout(this.timeout);
      if ((this.element.data as { usedBy: number }).usedBy === this.currentUser?.id) {
        this.updateElement({
          ...this.element,
          data: {
            ...this.element.data,
            usedBy: false,
          },
        });
      }
      this.$root.$emit('select', null);
      this.setDouble(false);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editor = this.editor as any;
    editor?.setOptions({ editable: false });
    editor?.blur();
    editor.setSelection();
  }

  onInput(state: { getJSON: () => void }) {
    this.resetTimer();
    this.updateElement({
      ...this.element,
      data: {
        ...this.element.data,
        text: state.getJSON(),
      },
    });
  }
}
